'use client'

import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { Menu, Play, X } from 'lucide-react'

import { cn } from '@/lib/utils'

const LINKS = [
    { label: 'Library', href: '#library' },
    { label: 'How it works', href: '#how' },
    { label: 'Stories', href: '#stories' }
]

export const Navbar: FC = (): ReactNode => {
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24)
        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-40 transition-all duration-300',
                scrolled
                    ? 'border-b border-black/8 bg-[#f5f5f7]/80 backdrop-blur-xl'
                    : 'border-b border-transparent bg-transparent'
            )}
        >
            <nav className='mx-auto flex h-16 max-w-6xl items-center justify-between px-4 md:px-6'>
                <a href='#top' className='flex items-center gap-2'>
                    <span className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-lg transition-colors duration-300',
                        scrolled ? 'bg-[#1a1a1a] text-white' : 'bg-white text-[#1a1a1a]'
                    )}>
                        <Play className={cn('size-4', scrolled ? 'fill-white' : 'fill-[#1a1a1a]')} />
                    </span>
                    <span className={cn(
                        'text-lg font-semibold tracking-tight transition-colors duration-300',
                        scrolled ? 'text-[#1a1a1a]' : 'text-white'
                    )}>
                        Stream Showcase
                    </span>
                </a>

                <ul className='hidden items-center gap-8 md:flex'>
                    {LINKS.map((link) => (
                        <li key={link.href}>
                            <a
                                href={link.href}
                                className={cn(
                                    'text-sm font-medium transition-colors duration-300',
                                    scrolled ? 'text-[#555] hover:text-[#1a1a1a]' : 'text-white/80 hover:text-white'
                                )}
                            >
                                {link.label}
                            </a>
                        </li>
                    ))}
                </ul>

                <div className='hidden items-center gap-3 md:flex'>
                    <a
                        href='#waitlist'
                        className={cn(
                            'text-sm font-medium transition-colors duration-300',
                            scrolled ? 'text-[#555] hover:text-[#1a1a1a]' : 'text-white/80 hover:text-white'
                        )}
                    >
                        Sign in
                    </a>
                    <a
                        href='#waitlist'
                        className={cn(
                            'rounded-full px-4 py-2 text-sm font-semibold transition-all hover:-translate-y-0.5',
                            scrolled ? 'bg-[#1a1a1a] text-white' : 'bg-white text-[#1a1a1a]'
                        )}
                    >
                        Start watching
                    </a>
                </div>

                <button
                    type='button'
                    aria-label={open ? 'Close menu' : 'Open menu'}
                    aria-expanded={open}
                    onClick={() => setOpen((v) => !v)}
                    className={cn(
                        'flex h-9 w-9 items-center justify-center rounded-lg md:hidden transition-colors duration-300',
                        scrolled ? 'text-[#1a1a1a]' : 'text-white'
                    )}
                >
                    {open ? <X className='size-5' /> : <Menu className='size-5' />}
                </button>
            </nav>

            {open && (
                <div className='border-t border-black/8 bg-[#f5f5f7]/95 backdrop-blur-xl md:hidden'>
                    <ul className='flex flex-col px-4 py-4'>
                        {LINKS.map((link) => (
                            <li key={link.href}>
                                <a
                                    href={link.href}
                                    onClick={() => setOpen(false)}
                                    className='block py-3 text-base font-medium text-[#1a1a1a]'
                                >
                                    {link.label}
                                </a>
                            </li>
                        ))}
                        <li className='pt-2'>
                            <a
                                href='#waitlist'
                                onClick={() => setOpen(false)}
                                className='block rounded-full bg-[#1a1a1a] px-4 py-3 text-center text-sm font-semibold text-white'
                            >
                                Start watching
                            </a>
                        </li>
                    </ul>
                </div>
            )}
        </header>
    )
}
